import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBars, FaTimes, FaGlobe } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';

const HeaderContainer = styled.header<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  background: ${props => {
    if (!props.$isHomePage) return 'white';
    return props.$isHeroSection ? 'transparent' : 'white';
  }};
  box-shadow: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all ${props => props.theme.transitions.normal};
  border-top: 2px solid transparent;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
  border-bottom: ${props => {
    if (!props.$isHomePage) return `2px solid ${props.theme.colors.border}`;
    return props.$isHeroSection ? '2px solid rgba(255, 255, 255, 0.3)' : `2px solid ${props.theme.colors.border}`;
  }};
`;

const HeaderWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled(Link)<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  display: flex;
  align-items: center;
  transition: opacity ${props => props.theme.transitions.normal};

  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  height: 40px;
  width: auto;
  filter: ${props => {
    if (!props.$isHomePage) return 'none';
    return props.$isHeroSection ? 'brightness(0) invert(1)' : 'none';
  }};
  transition: filter ${props => props.theme.transitions.normal};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 32px;
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 80%;
    max-width: 320px;
    background: white;
    transform: translateX(${props => props.$isOpen ? '0' : '100%'});
    transition: transform ${props => props.theme.transitions.normal};
    box-shadow: ${props => props.theme.shadows.xl};
    height: auto;
    z-index: 1001;
    overflow-y: auto;
  }
`;

const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  height: 100%;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
    gap: 0;
    width: 100%;
  }
`;

const NavItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    border-bottom: 1px solid ${props => props.theme.colors.border};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const NavLink = styled(Link)<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  color: ${props => {
    if (!props.$isHomePage) return props.theme.colors.text;
    return props.$isHeroSection ? 'white' : props.theme.colors.text;
  }};
  font-family: 'Pretendard', 'Apple SD Gothic Neo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: 0;
  height: 100%;
  text-decoration: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: space-between;
    padding: ${props => props.theme.spacing.lg};
    font-size: 1.1rem;
    color: ${props => props.theme.colors.text};
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  box-shadow: ${props => props.theme.shadows.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  min-width: 200px;
  padding: ${props => props.theme.spacing.sm} 0;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateX(-50%) translateY(10px)' : 'translateX(-50%) translateY(0)'};
  transition: all ${props => props.theme.transitions.fast};
  z-index: 1000;
  margin-top: ${props => props.theme.spacing.sm};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: static;
    transform: none;
    box-shadow: none;
    background: ${props => props.theme.colors.background};
    border-radius: 0;
    margin-top: 0;
    max-height: ${props => props.$isOpen ? '1000px' : '0'};
    overflow: hidden;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  font-family: 'Pretendard', 'Apple SD Gothic Neo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
    font-size: 0.85rem;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  height: 100%;
`;

const LangButton = styled.button<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: ${props => {
    if (!props.$isHomePage) return `1px solid ${props.theme.colors.border}`;
    return props.$isHeroSection ? '1px solid white' : `1px solid ${props.theme.colors.border}`;
  }};
  border-radius: ${props => props.theme.borderRadius.sm};
  background: ${props => {
    if (!props.$isHomePage) return 'white';
    return props.$isHeroSection ? 'transparent' : 'white';
  }};
  color: ${props => {
    if (!props.$isHomePage) return props.theme.colors.text;
    return props.$isHeroSection ? 'white' : props.theme.colors.text;
  }};
  font-family: 'Pretendard', 'Apple SD Gothic Neo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => {
      if (!props.$isHomePage) return props.theme.colors.background;
      return props.$isHeroSection ? 'rgba(255, 255, 255, 0.1)' : props.theme.colors.background;
    }};
  }
`;

const MenuButton = styled.button<{ $isHomePage: boolean; $isHeroSection: boolean }>`
  display: none;
  font-size: 1.5rem;
  color: ${props => {
    if (!props.$isHomePage) return props.theme.colors.text;
    return props.$isHeroSection ? 'white' : props.theme.colors.text;
  }};
  transition: color ${props => props.theme.transitions.fast};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeroSection, setIsHeroSection] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ko' ? 'en' : 'ko';
    i18n.changeLanguage(newLang);
  };

  // 슬라이드 감지를 위한 이벤트 리스너
  useEffect(() => {
    if (!isHomePage) {
      setIsHeroSection(false);
      return;
    }

    const handleSlideChange = (event: CustomEvent) => {
      // currentSlide가 0이면 Hero 섹션
      setIsHeroSection(event.detail.currentSlide === 0);
    };

    // HomePage에서 dispatch하는 커스텀 이벤트 리스닝
    window.addEventListener('slideChange' as any, handleSlideChange as any);

    // 초기값 설정 - 처음에는 Hero 섹션
    setIsHeroSection(true);

    return () => window.removeEventListener('slideChange' as any, handleSlideChange as any);
  }, [isHomePage]);

  const GlobeIcon = FaGlobe as IconType;
  const BarsIcon = FaBars as IconType;
  const TimesIcon = FaTimes as IconType;

  const handleMouseEnter = (menu: string) => {
    setOpenDropdown(menu);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const handleHomeClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <MobileOverlay $isOpen={isMenuOpen} onClick={closeMenu} />
      <HeaderContainer $isHomePage={isHomePage} $isHeroSection={isHeroSection}>
        <HeaderWrapper>
          <Logo to="/" onClick={handleHomeClick} $isHomePage={isHomePage} $isHeroSection={isHeroSection}>
            {/* Logo - Full version for both desktop and mobile */}
            <LogoImage
              src="/img/naw_mainlogo_full.png"
              alt="(주)나우바이오"
              $isHomePage={isHomePage}
              $isHeroSection={isHeroSection}
            />
          </Logo>

          <Nav $isOpen={isMenuOpen}>
          <NavList>
            {/* ABOUT US */}
            <NavItem
              onMouseEnter={() => handleMouseEnter('about')}
              onMouseLeave={handleMouseLeave}
            >
              <NavLink
                to="/about?tab=overview"
                onClick={closeMenu}
                $isHomePage={isHomePage}
                $isHeroSection={isHeroSection}
              >
                {t('nav.aboutUs')}
              </NavLink>
              <DropdownMenu $isOpen={openDropdown === 'about'}>
                <DropdownItem to="/about?tab=overview" onClick={closeMenu}>
                  {t('nav.about.overview')}
                </DropdownItem>
                <DropdownItem to="/about?tab=greeting" onClick={closeMenu}>
                  {t('nav.about.greeting')}
                </DropdownItem>
                <DropdownItem to="/about?tab=history" onClick={closeMenu}>
                  {t('nav.about.history')}
                </DropdownItem>
                <DropdownItem to="/about?tab=location" onClick={closeMenu}>
                  {t('nav.about.location')}
                </DropdownItem>
              </DropdownMenu>
            </NavItem>

            {/* PRODUCT */}
            <NavItem
              onMouseEnter={() => handleMouseEnter('product')}
              onMouseLeave={handleMouseLeave}
            >
              <NavLink
                to="/products"
                onClick={closeMenu}
                $isHomePage={isHomePage}
                $isHeroSection={isHeroSection}
              >
                {t('nav.product')}
              </NavLink>
              <DropdownMenu $isOpen={openDropdown === 'product'}>
                <DropdownItem to="/products/1" onClick={closeMenu}>
                  {t('nav.products.newpol')}
                </DropdownItem>
                <DropdownItem to="/products/2" onClick={closeMenu}>
                  {t('nav.products.nawtan')}
                </DropdownItem>
                <DropdownItem to="/products/3" onClick={closeMenu}>
                  {t('nav.products.gropro')}
                </DropdownItem>
                <DropdownItem to="/products/4" onClick={closeMenu}>
                  {t('nav.products.methaneking')}
                </DropdownItem>
                <DropdownItem to="/products/5" onClick={closeMenu}>
                  {t('nav.products.quillaja')}
                </DropdownItem>
                <DropdownItem to="/products/6" onClick={closeMenu}>
                  {t('nav.products.nawsan777')}
                </DropdownItem>
                <DropdownItem to="/products/7" onClick={closeMenu}>
                  {t('nav.products.molimprint')}
                </DropdownItem>
                <DropdownItem to="/products/8" onClick={closeMenu}>
                  {t('nav.products.molimaxae')}
                </DropdownItem>
                <DropdownItem to="/products/9" onClick={closeMenu}>
                  {t('nav.products.nawsweet3')}
                </DropdownItem>
              </DropdownMenu>
            </NavItem>

            {/* SUPPORT */}
            <NavItem
              onMouseEnter={() => handleMouseEnter('support')}
              onMouseLeave={handleMouseLeave}
            >
              <NavLink
                to="/inquiry"
                onClick={closeMenu}
                $isHomePage={isHomePage}
                $isHeroSection={isHeroSection}
              >
                {t('nav.support')}
              </NavLink>
              <DropdownMenu $isOpen={openDropdown === 'support'}>
                <DropdownItem to="/inquiry" onClick={closeMenu}>
                  {t('nav.supportMenu.inquiry')}
                </DropdownItem>
              </DropdownMenu>
            </NavItem>

            {/* NEWS */}
            <NavItem
              onMouseEnter={() => handleMouseEnter('news')}
              onMouseLeave={handleMouseLeave}
            >
              <NavLink
                to="/news"
                onClick={closeMenu}
                $isHomePage={isHomePage}
                $isHeroSection={isHeroSection}
              >
                {t('nav.news')}
              </NavLink>
              <DropdownMenu $isOpen={openDropdown === 'news'}>
                <DropdownItem to="/news" onClick={closeMenu}>
                  {t('nav.newsMenu.notice')}
                </DropdownItem>
              </DropdownMenu>
            </NavItem>
          </NavList>
        </Nav>

        <Controls>
          <LangButton onClick={toggleLanguage} $isHomePage={isHomePage} $isHeroSection={isHeroSection}>
            <GlobeIcon />
            {i18n.language === 'ko' ? 'EN' : 'KO'}
          </LangButton>
          <MenuButton onClick={toggleMenu} $isHomePage={isHomePage} $isHeroSection={isHeroSection}>
            {isMenuOpen ? <TimesIcon /> : <BarsIcon />}
          </MenuButton>
        </Controls>
      </HeaderWrapper>
    </HeaderContainer>
    </>
  );
};

export default Header;
